import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Palette, Check, FileText, Layout, Settings, Trash2, Star } from 'lucide-react';
import { useTheme } from '@shared/contexts/ThemeContext';
import { useLayout } from '@shared/contexts/LayoutContext';
import { AdminPanel } from '../components/AdminPanel';
import { TeamManagementPage } from './TeamManagementPage';
import { UserManager } from '../components/UserManager';
import { APP_VERSION, VERSION_HISTORY } from '@/version';
import type { Technician } from '@shared/types';

interface SettingsPageProps {
    technicians: Technician[];
    setTechnicians: React.Dispatch<React.SetStateAction<Technician[]>>;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ technicians, setTechnicians }) => {
    const navigate = useNavigate();
    const { theme, setTheme, availableThemes, deleteTheme, defaultTheme, setDefaultTheme } = useTheme();
    const { layoutMode, setLayoutMode } = useLayout();
    const [activeTab, setActiveTab] = useState<'general' | 'team' | 'users' | 'appearance' | 'changelog'>('general');

    return (
        <div className="min-h-screen p-4 md:p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                {layoutMode !== 'sidebar' && (
                    <button
                        onClick={() => navigate('/')}
                        className="p-2 hover:bg-glass-border/10 rounded-full transition-colors text-muted hover:text-main"
                        title="Retour au Tableau de Bord"
                    >
                        <ArrowLeft size={24} />
                    </button>
                )}
                <div className="flex flex-col">
                    <h1 className="text-2xl font-bold text-main flex items-center gap-3">
                        <Settings className="text-primary" />
                        Paramètres & Administration
                    </h1>
                    <p className="text-sm text-muted">Configuration système et gestion d'équipe</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 md:pb-0">
                <button
                    onClick={() => setActiveTab('general')}
                    className={`flex-1 md:flex-initial px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${activeTab === 'general'
                        ? 'bg-red-500/20 text-red-500 border border-red-500/50'
                        : 'bg-glass-panel text-muted border border-glass-border hover:bg-glass-border/10'
                        }`}
                >
                    <Shield size={20} />
                    <span>Système & Logs</span>
                </button>
                <button
                    onClick={() => setActiveTab('team')}
                    className={`flex-1 md:flex-initial px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${activeTab === 'team'
                        ? 'bg-purple-500/20 text-purple-500 border border-purple-500/50'
                        : 'bg-glass-panel text-muted border border-glass-border hover:bg-glass-border/10'
                        }`}
                >
                    <Users size={20} />
                    <span>Gestion d'Équipe</span>
                </button>
                <button
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 md:flex-initial px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${activeTab === 'users'
                        ? 'bg-blue-500/20 text-blue-500 border border-blue-500/50'
                        : 'bg-glass-panel text-muted border border-glass-border hover:bg-glass-border/10'
                        }`}
                >
                    <Users size={20} />
                    <span>Utilisateurs</span>
                </button>
                <button
                    onClick={() => setActiveTab('changelog')}
                    className={`flex-1 md:flex-initial px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${activeTab === 'changelog'
                        ? 'bg-amber-500/20 text-amber-500 border border-amber-500/50'
                        : 'bg-glass-panel text-muted border border-glass-border hover:bg-glass-border/10'
                        }`}
                >
                    <FileText size={20} />
                    <span>Versions</span>
                </button>
                <button
                    onClick={() => setActiveTab('appearance')}
                    className={`flex-1 md:flex-initial px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${activeTab === 'appearance'
                        ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/50'
                        : 'bg-glass-panel text-muted border border-glass-border hover:bg-glass-border/10'
                        }`}
                >
                    <Palette size={20} />
                    <span>Apparence</span>
                </button>
            </div>

            {/* Content */}
            <div className="transition-opacity duration-200">
                {activeTab === 'general' ? (
                    <AdminPanel />
                ) : activeTab === 'team' ? (
                    <TeamManagementPage
                        technicians={technicians}
                        setTechnicians={setTechnicians}
                    />
                ) : activeTab === 'users' ? (
                    <UserManager technicians={technicians} />
                ) : activeTab === 'changelog' ? (
                    <div className="glass-panel p-6">
                        <h2 className="text-xl font-bold text-main mb-6">Notes de Mise à Jour (v{APP_VERSION})</h2>
                        <div className="space-y-6">
                            {VERSION_HISTORY.map((v, idx) => (
                                <div key={idx} className="border-l-2 border-primary/30 pl-4 py-1 relative">
                                    <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-primary/20 border-2 border-primary"></div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-lg font-bold text-main">v{v.version}</span>
                                        <span className="text-sm text-muted">{v.date}</span>
                                        {idx === 0 && <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full border border-primary/30">Actuel</span>}
                                    </div>
                                    <ul className="list-disc list-inside space-y-1">
                                        {v.changes.map((change, cIdx) => (
                                            <li key={cIdx} className="text-sm text-muted">{change}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="glass-panel p-6 mb-6">
                        {/* Layout Mode Selection */}
                        <div className="mb-8 border-b border-glass-border pb-8">
                            <h3 className="text-lg font-semibold text-main mb-4 flex items-center gap-2">
                                <Layout size={20} className="text-emerald-400" />
                                Mode de Navigation
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Top Nav Option */}
                                <button
                                    onClick={() => setLayoutMode('top-nav')}
                                    className={`relative p-4 rounded-xl border-2 transition-all text-left group ${layoutMode === 'top-nav' ? 'border-primary bg-primary/10' : 'border-transparent bg-white/5 hover:bg-white/10'}`}
                                >
                                    <div className="h-24 rounded-lg bg-[#0f172a] mb-4 border border-white/10 relative overflow-hidden shadow-inner flex flex-col">
                                        <div className="h-6 w-full bg-white/10 border-b border-white/5"></div>
                                        <div className="flex-1 p-2 flex gap-2">
                                            <div className="w-1/4 h-full bg-white/5 rounded"></div>
                                            <div className="w-3/4 h-full bg-white/5 rounded"></div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="block font-medium text-main">Menu Haut</span>
                                            <span className="text-xs text-muted">Navigation Classique</span>
                                        </div>
                                        {layoutMode === 'top-nav' && <div className="bg-primary rounded-full p-1"><Check size={14} className="text-white" /></div>}
                                    </div>
                                </button>

                                {/* Sidebar Option */}
                                <button
                                    onClick={() => {
                                        setLayoutMode('sidebar');
                                        setTheme('dark'); // Auto-switch to dark theme as default for sidebar
                                    }}
                                    className={`relative p-4 rounded-xl border-2 transition-all text-left group ${layoutMode === 'sidebar' ? 'border-primary bg-primary/10' : 'border-transparent bg-white/5 hover:bg-white/10'}`}
                                >
                                    <div className="h-24 rounded-lg bg-[#0f172a] mb-4 border border-white/10 relative overflow-hidden shadow-inner flex">
                                        <div className="w-1/4 h-full bg-white/10 border-r border-white/5"></div>
                                        <div className="flex-1 p-2 flex flex-col gap-2">
                                            <div className="w-full h-4 bg-white/5 rounded"></div>
                                            <div className="w-full h-12 bg-white/5 rounded"></div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="block font-medium text-main">Menu Latéral</span>
                                            <span className="text-xs text-muted">Barre latérale rétractable</span>
                                        </div>
                                        {layoutMode === 'sidebar' && <div className="bg-primary rounded-full p-1"><Check size={14} className="text-white" /></div>}
                                    </div>
                                </button>
                            </div>
                        </div>

                        <h3 className="text-lg font-semibold text-main mb-4 flex items-center gap-2">
                            <Palette size={20} className="text-purple-400" />
                            C'est le thème
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {availableThemes.map((t) => {
                                // Helper to render the specific preview for each known theme
                                const renderPreview = () => {
                                    switch (t.id) {
                                        case 'dark':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#0f172a] mb-4 border border-white/10 relative overflow-hidden shadow-inner">
                                                    <div className="absolute top-4 left-4 right-4 h-8 bg-white/10 rounded backdrop-blur-md border border-white/5"></div>
                                                    <div className="absolute top-16 left-4 w-16 h-16 rounded-full bg-indigo-500/20 blur-xl"></div>
                                                </div>
                                            );
                                        case 'light':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#f8fafc] mb-4 border border-gray-200 relative overflow-hidden shadow-inner">
                                                    <div className="absolute top-4 left-4 right-4 h-8 bg-white/80 rounded border border-gray-200 backdrop-blur-sm"></div>
                                                </div>
                                            );
                                        case 'professional':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#ffffff] mb-4 border border-gray-300 relative overflow-hidden shadow-sm">
                                                    <div className="absolute top-4 left-4 right-4 h-8 bg-white rounded border border-gray-300 shadow-sm"></div>
                                                    <div className="absolute top-16 left-4 w-24 h-2 bg-gray-100 rounded"></div>
                                                    <div className="absolute top-20 left-4 w-16 h-2 bg-gray-100 rounded"></div>
                                                </div>
                                            );
                                        case 'hybrid':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#0f172a] mb-4 border border-white/10 relative overflow-hidden shadow-inner flex items-center justify-center">
                                                    <div className="w-3/4 h-3/4 bg-white rounded-lg shadow-lg relative overflow-hidden">
                                                        <div className="absolute top-0 left-0 right-0 h-4 bg-gray-100 border-b border-gray-200"></div>
                                                        <div className="absolute left-0 top-4 bottom-0 w-6 bg-gray-50 border-r border-gray-200"></div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="w-8 h-8 rounded bg-cyan-400 opacity-80 blur-[2px]"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        case 'sharp':
                                            return (
                                                <div className="h-32 bg-white mb-4 border border-black relative overflow-hidden flex items-center justify-center">
                                                    <div className="w-3/4 h-3/4 border border-black relative">
                                                        <div className="absolute top-0 left-0 right-0 h-4 border-b border-black bg-white"></div>
                                                        <div className="absolute left-0 top-4 bottom-0 w-6 border-r border-black bg-white"></div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="w-8 h-8 bg-black"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        case 'minimalist':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#f1f5f9] mb-4 border border-[#e2e8f0] relative overflow-hidden shadow-sm flex items-center justify-center">
                                                    <div className="w-3/4 h-3/4 bg-white rounded-lg shadow-sm border border-slate-200 relative overflow-hidden">
                                                        <div className="absolute top-0 left-0 right-0 h-4 bg-slate-50 border-b border-slate-100"></div>
                                                        <div className="absolute left-0 top-4 bottom-0 w-6 bg-white border-r border-slate-100"></div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="w-8 h-8 rounded bg-slate-700"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        case 'minimalist-dark':
                                            return (
                                                <div className="h-32 rounded-lg bg-[#0f172a] mb-4 border border-[#334155] relative overflow-hidden shadow-sm flex items-center justify-center">
                                                    <div className="w-3/4 h-3/4 bg-[#1e293b] rounded-lg shadow-sm border border-slate-700 relative overflow-hidden">
                                                        <div className="absolute top-0 left-0 right-0 h-4 bg-slate-900 border-b border-slate-800"></div>
                                                        <div className="absolute left-0 top-4 bottom-0 w-6 bg-[#1e293b] border-r border-slate-800"></div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="w-8 h-8 rounded bg-slate-400"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        default:
                                            return (
                                                <div className="h-32 rounded-lg bg-glass-panel mb-4 border border-glass-border flex items-center justify-center">
                                                    <Palette className="text-muted" size={32} />
                                                </div>
                                            );
                                    }
                                };

                                const isDefault = t.id === defaultTheme;
                                const isActive = theme === t.id;

                                return (
                                    <div
                                        key={t.id}
                                        className={`relative p-4 rounded-xl border-2 transition-all text-left group
                                        ${isActive
                                                ? 'border-primary bg-primary/10'
                                                : 'border-transparent bg-white/5 hover:bg-white/10'
                                            }`}
                                    >
                                        <div onClick={() => setTheme(t.id)} className="cursor-pointer">
                                            {renderPreview()}
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="block font-medium text-main">{t.name}</span>
                                                    <span className="text-xs text-muted">{t.description}</span>
                                                </div>
                                                {isActive && <div className="bg-primary rounded-full p-1"><Check size={14} className="text-white" /></div>}
                                            </div>
                                        </div>

                                        {/* Admin Actions Overlay */}
                                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {/* Set Default Button */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDefaultTheme(t.id);
                                                }}
                                                className={`p-1.5 rounded-lg transition-colors border ${isDefault
                                                    ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50'
                                                    : 'bg-black/50 text-gray-400 border-white/10 hover:text-yellow-400'
                                                    }`}
                                                title={isDefault ? "Thème par défaut" : "Définir comme défaut"}
                                            >
                                                <Star size={14} fill={isDefault ? "currentColor" : "none"} />
                                            </button>

                                            {/* Delete Button */}
                                            {availableThemes.length > 1 && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (confirm(`Supprimer le thème "${t.name}" ?`)) deleteTheme(t.id);
                                                    }}
                                                    disabled={isActive}
                                                    className={`p-1.5 rounded-lg transition-colors border border-white/10 ${isActive
                                                        ? 'bg-black/20 text-gray-600 cursor-not-allowed'
                                                        : 'bg-black/50 text-gray-400 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/30'
                                                        }`}
                                                    title={isActive ? "Impossible de supprimer le thème actif" : "Supprimer le thème"}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
